import Inputz from "../ui/atoms/Inputz";
import Buttonz from "../ui/atoms/Buttonz";
import TitleZ from "../ui/atoms/TitleZ";
import CountryDropDown from "../ui/atoms/DropDownzCountry";
import DropDownz from "../ui/atoms/DropDownz";
import TextAreaz from "../ui/atoms/TextAreaz";
import { useEffect, useState } from "react";
import "../styles/compo.scss";
import SocialMedia from "../ui/compoP/SocialMedia";
import ImageUploader from "../ui/atoms/ImageUploader";
import { useDispatch } from "react-redux";
import { signupSubmit } from "../auth/signupSubmit";
import AllPopups from "../ui/compoP/AllPopups";
import UserCard from "../ui/compoP/UserCard";
import SvgIcn from "../data/IconCompo";
import Switchz from "../ui/atoms/Switchz";
import { useParams, useLocation, useSearchParams, useNavigate } from "react-router-dom";
import LogoP from "../ui/atoms/LogoP";
import { getSignupRequest } from "../services/UserService";
import { validationPopup, hidePopup, showPopup } from "../redux/actions";
import { getSignupRequestBaseUrl } from "../config";
import NotFound from "./NotFound";

function Signup() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const legacyInvite =
    params.id != null && String(params.id).trim() !== "";
  const queryRid = searchParams.get("rid");
  const queryToken = searchParams.get("token");
  const hasVerifiedSignupParams =
    Boolean(queryRid?.trim()) && Boolean(queryToken?.trim());
  const accessAllowed = legacyInvite || hasVerifiedSignupParams;
  const [data, setData] = useState({});
  const [errors, setErrors] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [signupFlow, setSignupFlow] = useState({
    rid: null,
    token: null,
    expiresAtMs: null,
  });
  const [signupExpiredHandled, setSignupExpiredHandled] = useState(false);
  const [signupLinkState, setSignupLinkState] = useState({
    checking: false,
    valid: true,
    message: "",
  });
  const [visibleInputs, setVisibleInputs] = useState({
    figma: false,
    dribbble: false,
    behance: false,
    linkedin: false,
    x: false,
    instagram: false,
    facebook: false,
    tiktok: false,
  });
  const [socialOrder, setSocialOrder] = useState([]);

  useEffect(() => {
    if (!params.id) return undefined;

    let intervalId;
    const fetchSignupRequest = async () => {
      try {
        const response = await getSignupRequest(params.id);
        if (response?.data?.data) {
          const createdAt = new Date(response.data.data.createdAt);
          const expiryDate = new Date(
            createdAt.getTime() + 2 * 24 * 60 * 60 * 1000
          );

          intervalId = setInterval(() => {
            const now = new Date();
            const difference = expiryDate - now;

            if (difference <= 0) {
              setTimeLeft("Expired");
              if (intervalId) clearInterval(intervalId);
            } else {
              const days = Math.floor(difference / (1000 * 60 * 60 * 24));
              const hours = Math.floor(
                (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
              );
              const minutes = Math.floor(
                (difference % (1000 * 60 * 60)) / (1000 * 60)
              );
              const seconds = Math.floor((difference % (1000 * 60)) / 1000);

              setTimeLeft(`${days}d ${hours}h ${minutes}m ${seconds}s`);
            }
          }, 1000);
        }
      } catch (error) {
        console.error("Error fetching signup request:", error);
      }
    };

    fetchSignupRequest();
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [params.id]);

  useEffect(() => {
    if (!accessAllowed) {
      setData((prev) => {
        const next = { ...prev };
        delete next.email;
        delete next.signup_request_id;
        delete next.signupRid;
        delete next.signupToken;
        return next;
      });
      setSignupFlow({ rid: null, token: null, expiresAtMs: null });
      setSignupExpiredHandled(false);
      setSignupLinkState({ checking: false, valid: true, message: "" });
      return;
    }

    const fromInviteRoute =
      params.email != null && params.email !== ""
        ? decodeURIComponent(params.email)
        : null;
    const queryEmail = searchParams.get("email");
    const fromNavigation =
      [location.state?.email, queryEmail].find(
        (v) => v != null && String(v).trim() !== ""
      ) || null;
    const resolvedEmail =
      (fromInviteRoute ?? (fromNavigation ? String(fromNavigation).trim() : null)) ||
      undefined;

    const rid = searchParams.get("rid");
    const token = searchParams.get("token");
    const hasSignupRequest = rid && token;

    // If the user arrived here from the email confirm link, ensure any open popup is closed.
    if (hasSignupRequest) {
      dispatch(hidePopup());
      try {
        localStorage.setItem("uud:closeSignupPopup", String(Date.now()));
        // auto-clear so it doesn't permanently block opening the popup later
        setTimeout(() => {
          try {
            localStorage.removeItem("uud:closeSignupPopup");
          } catch {
            // ignore
          }
        }, 1000);
      } catch {
        // ignore
      }
    }

    setData((prev) => ({
      ...prev,
      ...(params.id != null && params.id !== ""
        ? { signup_request_id: params.id }
        : {}),
      ...(resolvedEmail ? { email: resolvedEmail } : {}),
      ...(hasSignupRequest ? { signupRid: rid, signupToken: token } : {}),
    }));

    if (hasSignupRequest) {
      const base = getSignupRequestBaseUrl();
      setSignupLinkState({ checking: true, valid: true, message: "" });
      // Best-effort validation. If the status endpoint is missing/unreachable,
      // allow the page to open but the final submit will still validate again.
      const fallback = Date.now() + 15 * 60 * 1000;
      setSignupFlow({ rid, token, expiresAtMs: fallback });
      setSignupExpiredHandled(false);
      (async () => {
        try {
          const res = await fetch(`${base}/signup-request/status`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ rid, token }),
          });
          const json = await res.json().catch(() => null);
          const ok = Boolean(json?.ok);
          const expired = Boolean(json?.expired);
          const status = typeof json?.status === "string" ? json.status : "";
          const ms = json?.expiresAt ? new Date(String(json.expiresAt)).getTime() : null;

          if (!res.ok || !ok || expired || (status && status !== "verified")) {
            setSignupLinkState({
              checking: false,
              valid: false,
              message: "Signup link expired or cancelled. Please request a new link.",
            });
            return;
          }

          setSignupLinkState({ checking: false, valid: true, message: "" });
          if (ms && Number.isFinite(ms)) {
            setSignupFlow({ rid, token, expiresAtMs: ms });
          }
        } catch {
          // If the status endpoint is not deployed yet, don't block the page.
          setSignupLinkState({ checking: false, valid: true, message: "" });
        }
      })();
    } else {
      setSignupFlow({ rid: null, token: null, expiresAtMs: null });
      setSignupExpiredHandled(false);
      setSignupLinkState({ checking: false, valid: true, message: "" });
    }
  }, [
    accessAllowed,
    params.id,
    params.email,
    location.state,
    searchParams,
  ]);

  useEffect(() => {
    if (!signupFlow.rid) return;
    if (signupLinkState.checking || signupLinkState.valid) return;

    dispatch(showPopup("signupExpired"));
    navigate("/", { replace: true });
  }, [
    dispatch,
    navigate,
    signupFlow.rid,
    signupLinkState.checking,
    signupLinkState.valid,
  ]);

  useEffect(() => {
    if (!signupFlow.expiresAtMs) return undefined;
    const intervalId = setInterval(() => {
      const diff = signupFlow.expiresAtMs - Date.now();
      if (diff <= 0) {
        setTimeLeft("Expired");
        clearInterval(intervalId);
        if (!signupExpiredHandled && signupFlow.rid && signupFlow.token) {
          setSignupExpiredHandled(true);
          const base = getSignupRequestBaseUrl();
          fetch(`${base}/signup-request/cancel`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ rid: signupFlow.rid, token: signupFlow.token }),
          }).catch(() => {});
          dispatch(showPopup("signupExpired"));
          navigate("/", { replace: true });
        }
      } else {
        const minutes = Math.floor(diff / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setTimeLeft(`${minutes}m ${seconds}s`);
      }
    }, 1000);
    return () => clearInterval(intervalId);
  }, [
    signupFlow.expiresAtMs,
    signupFlow.rid,
    signupFlow.token,
    signupExpiredHandled,
    dispatch,
    navigate,
  ]);

  const handleChange = (e) => {
    const { name, value } = e;
    setData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => {
      return { ...prev, [`${name}`]: "" };
    });
    console.log({
      ...data,
      [name]: value,
    });
  };

  const handleSubmit = async () => {
    if (signupFlow.rid && !signupLinkState.valid) {
      dispatch(
        validationPopup({
          message:
            signupLinkState.message ||
            "Signup link expired. Please request a new link.",
          icon: "horror",
          type: "error",
        })
      );
      return;
    }
    if (signupFlow.expiresAtMs && signupFlow.expiresAtMs <= Date.now()) {
      dispatch(
        validationPopup({
          message: "Signup link expired. Please request a new link.",
          icon: "horror",
          type: "error",
        })
      );
      setErrors({ email: "Link expired" });
      return;
    }
    const newErrors = await signupSubmit(dispatch, data, "en", visibleInputs);
    setErrors(newErrors);
  };

  if (!accessAllowed) {
    return <NotFound />;
  }

  const isSignupLinkBroken =
    Boolean(signupFlow.rid) &&
    !signupLinkState.checking &&
    !signupLinkState.valid;

  if (isSignupLinkBroken) {
    return null;
  }

  return (
    <div
      className={`signup-hero${
        signupLinkState.checking ? " signup-hero--no-preview" : ""
      }`}
    >
      {!signupLinkState.checking ? (
        <div className="signup-usercard-box">
          <UserCard
            data={data}
            visibleInputs={visibleInputs}
            socialOrder={socialOrder}
            showSocialPlaceholder={true}
          />
        </div>
      ) : null}

      <div className="signup-page-box">
        {signupFlow.rid && signupLinkState.checking && (
          <div className="form-box">
            <TitleZ
              title="Signup"
              text={<>Validating signup link…</>}
            />
          </div>
        )}

        {signupFlow.rid && signupLinkState.checking
          ? null
          : (
          <>
            <div className="form-box">
              <LogoP
                logo="logo01"
                hoverLogo="logo03"
                logoTypeEn="logotype_en"
                logoTypeJp="logotype_jp"
                style={{ width: "72px" }}
                className="signup-logo"
              />
            </div>
            <div className="form-box">
              <TitleZ
                title="Signup"
                text={
                  <>
                    {params.id ? (
                      <>
                        Complete the form below to sign up before your invitation
                        expires.
                        {timeLeft && (
                          <div>
                            Time Remaining: <span>{timeLeft}</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        Complete the form below to create your account.
                        {signupFlow.rid && timeLeft && (
                          <div>
                            Time Remaining: <span>{timeLeft}</span>
                          </div>
                        )}
                      </>
                    )}
                    Please note that all fields are required.
                  </>
                }
              />
            </div>
            <div className="form-box">
              <ImageUploader
                addIcon={<SvgIcn name={"error"} />}
                deleteIcon={<SvgIcn name={"error"} />}
                onChange={(value) =>
                  handleChange({ name: "image", value: value })
                }
                error={errors?.image}
                onDelete={() => {
                  handleChange({ name: "image", value: undefined });
                }}
                className="avatar" // Pass error for profile image
              />
            </div>
            <div className="form-box">
              <Inputz
                iconL="mail"
                focusL="mail"
                iconR="lock"
                focusR="cal"
                hovericonR="mail"
                label="Email address"
                type="email"
                placeholder="Email address"
                name="email"
                value={data.email}
                disable={Boolean(params.email) || Boolean(signupFlow.rid)}
                onChange={handleChange}
                error={errors?.email} // Pass error for email
              />
              <Inputz
                iconL="password"
                iconR="ok"
                focusR="cal"
                iconOn="eye_on"
                iconOff="eye_off"
                placeholder="Password"
                type="password"
                name="password"
                value={data.password}
                onChange={handleChange}
                error={errors?.password} // Pass error for password
              />
            </div>

        <div className="form-box">
          <div className="text-hint-box">
            Username can only include letters, numbers, and underscores. (max 16
            characters)
          </div>

          <Inputz
            iconL="username"
            label="Username"
            type="text"
            placeholder="Username"
            permanentPlaceholder="uud.io/"
            textCounter={16}
            usernameSlug={true}
            name="username"
            value={data.username}
            onChange={handleChange}
            error={errors?.username}
            className="username"
          />
          <Inputz
            iconL="user"
            label="Name"
            type="text"
            placeholder="Your Name"
            name="fullName"
            internationalName={true}
            textCounter={20}
            value={data.fullName}
            onChange={handleChange}
            error={errors?.fullName} // Pass error for full name
          />
          <CountryDropDown
            iconL="flag"
            focusL="cal"
            iconR="drop_down"
            iconRHover="drop_up"
            iconClear="clear"
            focusR="cal"
            hoverIconR="mail"
            placeholder="Your Laction"
            onSelect={(e) => {
              handleChange(e);
            }}
            value={data.nationality}
            name="nationality"
            error={errors?.nationality}
          />
          <DropDownz
            iconL="bag"
            focusL="bag"
            iconR="drop_down"
            iconRHover="drop_up"
            iconClear="clear"
            focusR="cal"
            hoverIconR="mail"
            placeholder="Your Role"
            className="custom-input"
            onSelect={handleChange}
            name="subRole"
            error={errors?.subRole} // Pass error for role
          >
              <span>Product Designer</span>
              <span>UXUI Designer</span>
              <span>UX Designer</span>
              <span>UI Designer</span>
              <span>Others - Designer</span>
              <span>Researcher</span>
              <span>Frontend Developer</span>
              <span>Backend Developer</span>
              <span>Full Stack Developer</span>
              <span>Excutive</span>
              <span>Director</span>
              <span>Manager</span>
              <span>Marketer</span>
              <span>HR - Recruiter</span>
              <span>Student</span>
              <span>Others - Tech</span>
          </DropDownz>
          <div className="experience-box">
            <p>Years of experience:</p>
            <Inputz
              iconL="cal"
              // focusL="mail"
              // label="Years of Experience"
              type="text"
              placeholder="0"
              name="experience"
              numbersOnly={true}
              value={data.experience || ""}
              onChange={handleChange}
              error={errors?.experience} // Pass error for experience
            />
          </div>
          <TextAreaz
            className="insider"
            title="Introduction"
            iconL="namecard"
            iconR="clear"
            value={data.intro || ""}
            maxLength={200}
            onChange={handleChange}
            name="intro"
            error={errors?.intro} // Pass error for introduction
          />
        </div>
        <div className="form-box">
          <SocialMedia
            iconOn="instagram_line"
            iconOff="instagram_line"
            data={data}
            handleChange={handleChange}
            visibleInputs={visibleInputs}
            setVisibleInputs={setVisibleInputs}
            errors={errors}
            setData={setData}
            socialOrder={socialOrder}
            setSocialOrder={setSocialOrder}
          />

          <div className="text-hint-box">
            Add your website, portfolio, and your personal content links.
          </div>

          <Inputz
            iconL="globe"
            // focusL="mail"
            hovericonR="mail"
            label="Email address"
            type="email"
            placeholder="Personal Website / Profile"
            onChange={handleChange}
            value={data.personalSite}
            name="personalSite"
            error={errors?.personalSite}
          />

          <Inputz
            iconL="blog_line"
            // focusL="mail"
            hovericonR="mail"
            label="Email address"
            type="email"
            placeholder="Personal Blog / Vlog / podcast"
            onChange={handleChange}
            value={data.personalBlog}
            name="personalBlog"
            error={errors?.personalBlog}
          />
        </div>

        <div className="form-box">
          <Switchz
            hintText={
              <div className="hint-text">
                By signing up, I agree to <a href="#">Terms & Privacy</a>.
              </div>
            }
            onText="Agree"
            offText="Agree?"
            value={data?.terms}
            width="100px"
            onIcon="ok"
            direction="row-reverse"
            className="SignupSwitch"
            name="terms"
            onToggle={handleChange}
            error={errors?.terms}
          />
        </div>
        <div className="form-box">
          <Buttonz
            iconR="login"
            hoverR="login"
            label="Signup"
            onClick={handleSubmit}
          />
          {signupFlow.rid && (
            <Buttonz
              label="Abort"
              className="secondary lined"
              onClick={async () => {
                const base = getSignupRequestBaseUrl();
                try {
                  await fetch(`${base}/signup-request/cancel`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      rid: signupFlow.rid,
                      token: signupFlow.token,
                    }),
                  });
                } catch {
                  // ignore
                }
                dispatch(
                  validationPopup({
                    message: "Signup cancelled.",
                    icon: "horror",
                    type: "error",
                  })
                );
                window.location.href = "/";
              }}
            />
          )}
        </div>

            <AllPopups />
          </>
        )}
      </div>
    </div>
  );
}

export default Signup;
